import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePathParam, usePagination } from '@chilecompra/react-kit'
import { Card, Skeleton, Table, TableCell, Toast, Typography, Pagination } from '@chilecompra/react-kit/components'
import { checkIfForeignDni } from '../../modules/utils/helpers'
import { onGetTaxAnnotationsThunk } from './TaxAnnotationsSection.actions'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import {
  AnnotationsToast,
  Column,
  Content,
  Paragraph,
  Row,
  Separator,
  TableCellName,
  TableRow
} from './TaxAnnotationsSection.styles'

/**
 * The tax annotations section container.
 */
const TaxAnnotationsSection = () => {
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const isForeign = checkIfForeignDni(dni)
  const { sellerType } = useSelector(state => state.basicSellerInformation)
  const { error, loading, annotations } = useSelector(state => state.taxAnnotations)
  const rowsPerPage = 5

  useEffect(() => {
    if (!loading) {
      return
    }

    if (!isForeign) {
      dispatch(onGetTaxAnnotationsThunk({ dni }))
    }
  }, [sellerType])

  const { page, totalPages, records, onChangePage, onGetCurrentPage } = usePagination({
    data: annotations,
    initialPage: 1,
    initialRowsPerPage: rowsPerPage
  })

  return (
    <Content>
      <Card color="blank" enableBorder margin="0" padding="32px 24px 8px">
        <Row>
          <Column width="200px">
            <Typography fontWeight="bold" padding="0 0 3px" variant="h4">
              Anotaciones tributarias
            </Typography>
            <Typography variant="subtitle">Fuente: Servicio de Impuestos Internos</Typography>
          </Column>
          {!isForeign && (
            <Column flexGrow={2} flexBasis="60%">
              {!error && !loading && (
                <AnnotationsToast severity="default">
                  Ante discrepancias consultar directamente con la fuente Servicio de Impuestos Internos.
                </AnnotationsToast>
              )}
              <Table accessibility={{ label: 'Anotaciones tributarias' }} padding="18px 0 16px 0">
                <ProtectByFeatures rule={features => features['com.seller/tax.annotations']}>
                  <TableRow>
                    <TableCell colSpan={2} disableBottomBorder>
                      {loading && (
                        <>
                          <Table accessibility={{ label: 'Anotaciones tributarias' }}>
                            <TableRow>
                              <TableCell disableBottomBorder enableHead padding="0 0 16px">
                                Nombre
                              </TableCell>
                              <TableCell alignment="right" disableBottomBorder enableHead padding="0 0 16px">
                                Descripción
                              </TableCell>
                            </TableRow>
                          </Table>
                          <Skeleton height="30px" variant="rect" width="100%" />
                        </>
                      )}
                      {!loading && !error && (
                        <Table accessibility={{ label: 'Anotaciones tributarias' }}>
                          <TableRow>
                            <TableCell
                              disableBottomBorder={annotations.length > 0}
                              enableHead
                              alignment="left"
                              width="92px"
                              padding={annotations.length > 0 ? '0 0 8px' : '0 0 8px 3px'}
                            >
                              Nombre
                            </TableCell>
                            <TableCell
                              disableBottomBorder={annotations.length > 0}
                              alignment="left"
                              enableHead
                              padding={annotations.length > 0 ? '0 0 8px' : '0 0 8px 3px'}
                            >
                              Descripción
                            </TableCell>
                          </TableRow>
                          {onGetCurrentPage().map((annotation, index) => {
                            return (
                              <TableRow key={index}>
                                <TableCellName disableBottomBorder padding="0 5px 9px 0">
                                  {annotation.annotationTitle}
                                </TableCellName>
                                <TableCell alignment="left" disableBottomBorder padding="0 0 9px 0">
                                  {annotation.annotationReasons.map((reason, index) => {
                                    return <Paragraph key={index}>{reason}</Paragraph>
                                  })}
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </Table>
                      )}
                      {annotations.length > rowsPerPage && <Separator />}
                      {!loading && !error && annotations.length === 0 && (
                        <Typography fontSize="12px" variant="subtitle" padding="8px 0 0 5px">
                          Por el momento no contamos con información disponible de esta empresa proveedora.
                        </Typography>
                      )}
                      {!loading && !error && totalPages > 1 && (
                        <Pagination
                          alignment="right"
                          size="small"
                          disableFirstButton={false}
                          disableLastButton={false}
                          disableResults={false}
                          labels={{
                            of: 'de',
                            results: 'resultados',
                            showing: 'Mostrando'
                          }}
                          total={annotations.length}
                          records={records}
                          onChange={onChangePage}
                          count={totalPages}
                          page={page}
                        />
                      )}
                      {!loading && error && (
                        <Toast
                          disableBackground
                          margin="0 0 0 -17px"
                          severity="default"
                          title="Lo sentimos, estamos presentando problemas con nuestro servidor."
                        >
                          En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en
                          unos minutos más.
                        </Toast>
                      )}
                    </TableCell>
                  </TableRow>
                </ProtectByFeatures>
              </Table>
            </Column>
          )}
          {isForeign && (
            <Column flexBasis="60%" flexGrow={2}>
              <Table accessibility={{ label: 'Información del proveedor' }}>
                <TableRow>
                  <TableCell padding="0 0 8px">
                    <Typography variant="body2">Sin información disponible.</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell disableBottomBorder padding="12px 0 16px">
                    <Typography variant="subtitle">
                      No tenemos información disponible de esta empresa proveedora extranjera.
                    </Typography>
                  </TableCell>
                </TableRow>
              </Table>
            </Column>
          )}
        </Row>
      </Card>
    </Content>
  )
}

export default TaxAnnotationsSection
